// packages/ui-kit/src/primitives/Drawer.tsx
// C3F v18 — reference: design/system/components/feedback/Drawer.{jsx,prompt.md}
import { useEffect, useRef, type ReactNode } from "react";
import { useBodyScrollLock } from "../lib/useBodyScrollLock.js";
import { useEnterExit } from "../lib/useEnterExit.js";

type Side = "right" | "left";
type Size = "sm" | "md" | "lg";

interface Props {
  open: boolean;
  onClose: () => void;
  side?: Side;
  title?: string;
  size?: Size;
  /** Action row pinned under the scrolling content (DS Drawer `footer`). */
  footer?: ReactNode;
  children: ReactNode;
}

const sizeWide: Record<Size, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  onClose,
  side = "right",
  title,
  size = "md",
  footer,
  children,
}: Props) {
  useBodyScrollLock(open);
  // Drawer panels move on the DS slow beat (320ms), unlike dialogs (200ms).
  const { render, show } = useEnterExit(open, 320);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Focus management. `aria-modal="true"` tells assistive tech that everything
   * outside this dialog does not exist — so without a trap the Tab key walked
   * into content the screen reader had just been told to ignore, and the reader
   * tabbed in silence. Escape alone was also the only way out, and nothing
   * announced it.
   */
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!render) return null;
  // Mobile (<sm): bottom-sheet. Desktop: side drawer, rounded on the inner edge
  // only (DS: the panel is flush with the screen edge it slides from).
  const sideCls =
    side === "right"
      ? "sm:right-0 sm:top-0 sm:bottom-0 sm:left-auto sm:h-full sm:w-full sm:rounded-l-xl sm:rounded-r-none"
      : "sm:left-0 sm:top-0 sm:bottom-0 sm:right-auto sm:h-full sm:w-full sm:rounded-r-xl sm:rounded-l-none";
  // Hidden offset: mobile slides up from the bottom; desktop slides in from the side.
  const hiddenCls =
    side === "right"
      ? "opacity-0 translate-y-full sm:translate-y-0 sm:translate-x-full"
      : "opacity-0 translate-y-full sm:translate-y-0 sm:-translate-x-full";
  // Enter rides the DS keyframes (px-up bottom-sheet, px-in side panel); their
  // --dur-slow collapses to 1ms under prefers-reduced-motion. Exit transitions
  // back to the hidden offset. px-in enters from the right, so the left drawer
  // keeps its transition-based enter instead of sliding the wrong way.
  const shownCls =
    "opacity-100 translate-y-0 sm:translate-x-0 [animation:px-up_var(--dur-slow)_var(--ease-out)_both] " +
    (side === "right"
      ? "sm:[animation:px-in_var(--dur-slow)_var(--ease-out)_both]"
      : "sm:[animation:none]");
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`fixed inset-0 z-50 bg-overlay transition-opacity duration-slow ease-out motion-reduce:transition-none ${
        show ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`absolute bottom-0 left-0 right-0 sm:bottom-auto sm:top-0 ${sideCls} ${sizeWide[size]} flex max-h-[90vh] flex-col overflow-hidden rounded-t-xl bg-elevated text-fg-default shadow-xl transition duration-slow ease-out motion-reduce:transition-none motion-reduce:transform-none sm:max-h-none ${
          show ? shownCls : hiddenCls
        }`}
      >
        {/* Grab handle: mobile bottom-sheet affordance only */}
        <div aria-hidden className="flex justify-center pb-1 pt-2 sm:hidden">
          <span className="h-1 w-11 rounded-pill bg-border-strong" />
        </div>
        {title && (
          <header className="flex flex-shrink-0 items-start justify-between gap-3 px-5 pb-2 pt-1 sm:p-6 sm:pb-2">
            <h2 className="text-h4 font-semibold">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="tap-target -mr-1 -mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm text-lede leading-none text-fg-muted transition-colors duration-fast hover:bg-hover hover:text-fg-default"
            >
              {"×"}
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-6 sm:pt-3">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-shrink-0 gap-2.5 px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-1 sm:px-6 sm:pb-6">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
